import {
  Box,
  ButtonBase,
  Paper,
  Typography,
  keyframes,
  styled,
} from '@mui/material';

import {
  AI_GLOW_SHADOW,
  aiBrandGradient,
  aiBrandSweep,
} from '@/utils/aiBrandGradient';

const BRAND_GRADIENT = aiBrandGradient(135);

const LAUNCHER_RADIUS = 8;

export const Surface = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'isExpanded',
})<{ isOpen?: boolean; isExpanded?: boolean }>(({ isOpen, isExpanded }) => ({
  position: 'fixed',
  right: 24,
  bottom: 24,
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  width: isExpanded ? '45vw' : '32vw',
  minWidth: isExpanded ? 680 : 480,
  maxWidth: isExpanded ? 900 : 600,
  height: isExpanded ? '95vh' : '82vh',
  maxHeight: isExpanded ? 960 : 800,
  padding: 0,
  overflow: 'hidden',
  borderRadius: 12,
  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.16), 0 2px 8px rgba(0, 0, 0, 0.08)',
  transformOrigin: 'bottom right',
  opacity: isOpen ? 1 : 0,
  transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.985)',
  pointerEvents: isOpen ? 'auto' : 'none',
  transition:
    'opacity 240ms cubic-bezier(0.22, 1, 0.36, 1), transform 280ms cubic-bezier(0.22, 1, 0.36, 1), width 280ms cubic-bezier(0.22, 1, 0.36, 1), height 280ms cubic-bezier(0.22, 1, 0.36, 1), min-width 280ms cubic-bezier(0.22, 1, 0.36, 1), max-width 280ms cubic-bezier(0.22, 1, 0.36, 1), max-height 280ms cubic-bezier(0.22, 1, 0.36, 1)',
}));

export const Footer = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
}));

export const Disclaimer = styled(Typography)(({ theme }) => ({
  display: 'block',
  padding: '0 24px 12px',
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: 11,
  lineHeight: 1.4,
}));

export const Launcher = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'isHidden',
})<{ isHidden?: boolean }>(({ isHidden }) => ({
  position: 'fixed',
  right: 24,
  bottom: 24,
  zIndex: 1301,
  borderRadius: LAUNCHER_RADIUS,
  boxShadow: AI_GLOW_SHADOW,
  willChange: 'transform, opacity',
  opacity: isHidden ? 0 : 1,
  transform: isHidden ? 'translateY(10px)' : 'translateY(0)',
  pointerEvents: isHidden ? 'none' : 'auto',
  transition: isHidden
    ? 'opacity 140ms ease, transform 140ms ease'
    : 'box-shadow 180ms ease, opacity 280ms cubic-bezier(0.22, 1, 0.36, 1) 120ms, transform 320ms cubic-bezier(0.22, 1, 0.36, 1) 120ms',
  '&:active': {
    transform: isHidden ? 'translateY(10px)' : 'translateY(1px)',
  },
}));

export const HeaderRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  height: 56,
  flexShrink: 0,
  padding: '0 12px',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const HeaderTitle = styled(Typography)({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  fontWeight: 600,
});

export const HeaderTitleText = styled('div')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const quickActionBackground = (fillAlpha: number, borderLayer: string) =>
  [
    `${aiBrandSweep(fillAlpha, 90)} padding-box`,
    'linear-gradient(90deg, #fff 0%, #fff 100%) padding-box',
    borderLayer,
  ].join(', ');

const QUICK_ACTION_GRADIENT = quickActionBackground(0.03, '#eee border-box');
const QUICK_ACTION_GRADIENT_HOVER = quickActionBackground(
  0.05,
  `${BRAND_GRADIENT} border-box`,
);

const TOP_GLOW_GRADIENT = aiBrandGradient(172.64);

const welcomeFadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const welcomeFadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(6px);
  }
`;

export const ChatBodyRoot = styled(Box)({
  position: 'relative',
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

export const WelcomeRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'exiting',
})<{ exiting?: boolean }>(({ theme, exiting }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 32,
  backgroundColor: theme.palette.background.paper,
  padding: '16px 24px 0',
  overflow: 'hidden',
  pointerEvents: exiting ? 'none' : 'auto',
  animation: exiting
    ? `${welcomeFadeOut} 180ms ease both`
    : `${welcomeFadeUp} 320ms cubic-bezier(0.22, 1, 0.36, 1) 80ms both`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
}));

const userMessageEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const aiTurnEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const threadFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const MessageEnter = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isEntering' && prop !== 'variant',
})<{ isEntering?: boolean; variant?: 'user' | 'ai' }>(
  ({ isEntering, variant }) => ({
    ...(isEntering && {
      animation: `${
        variant === 'user' ? userMessageEnter : aiTurnEnter
      } ${variant === 'user' ? 450 : 280}ms cubic-bezier(0.22, 1, 0.36, 1) both`,
    }),
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  }),
);

export const TopGlow = styled(Box)({
  position: 'absolute',
  top: -31,
  left: 0,
  width: '100%',
  height: 62,
  background: TOP_GLOW_GRADIENT,
  opacity: 0.1,
  filter: 'blur(32px)',
  pointerEvents: 'none',
});

export const WelcomeHeader = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
  width: '100%',
});

export const HeadingGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
  width: '100%',
});

export const Suggestions = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  width: '100%',
});

export const QuickAction = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  minHeight: 44,
  padding: '6px 16px',
  // Transparent border so the default and hover states share the same box
  // metrics; the visible border ring comes from the background's border-box
  // layer (#eee by default, brand gradient on hover).
  border: '1px solid transparent',
  borderRadius: 8,
  background: QUICK_ACTION_GRADIENT,
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',
  transition: 'background 120ms ease',
  '&:hover': {
    background: QUICK_ACTION_GRADIENT_HOVER,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.ai.main}`,
    outlineOffset: 2,
  },
}));

export const QuickActionIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  '& svg': { fontSize: 20 },
}));

/* ------------------------------------------------------------------ *
 * Conversation history (ConversationHistoryList)
 * ------------------------------------------------------------------ */

export const HistoryRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflowY: 'auto',
  backgroundColor: theme.palette.background.paper,
  padding: '8px 0',
}));

export const Centered = styled(Box)({
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
});

export const GroupLabel = styled(Typography)(({ theme }) => ({
  padding: '16px 24px 4px',
  color: theme.palette.text.disabled,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
}));

export const RowActions = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'alwaysVisible',
})<{ alwaysVisible?: boolean }>(({ alwaysVisible }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  opacity: alwaysVisible ? 1 : 0,
  pointerEvents: alwaysVisible ? 'auto' : 'none',
  transition: 'opacity 120ms ease',
  outline: 'none',
}));

export const Row = styled('div', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  width: '100%',
  padding: '2px 12px 2px 24px',
  background: active ? theme.palette.action.selected : 'transparent',
  transition: 'background-color 120ms ease',
  '&:hover': { backgroundColor: theme.palette.action.hover },
  '&:hover .row-actions, &:focus-within .row-actions': {
    opacity: 1,
    pointerEvents: 'auto',
  },
}));

export const RowSelectButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  padding: '8px 0',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.ai.main}`,
    outlineOffset: -2,
  },
}));

export const RowLabel = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});
